/**
 * The blood-drop marker that every reference-range bar draws as a Highcharts
 * data label.
 *
 * The width/height attributes here are load-bearing, not decoration. These
 * labels are `useHTML: true`, so Highcharts positions them by measuring the
 * rendered element's box and then CACHES that measurement on the label. An
 * <img> with no declared dimensions measures 0x0 until its bitmap has been
 * decoded, so when a chart renders before the drop image is decoded,
 * `verticalAlign: 'middle'` centers a zero-height box on the bar and the drop
 * settles ~6px lower — visibly hanging off the bottom of the bar. Nothing
 * corrects it afterwards: the cached box survives later re-renders (including
 * the delayed setState in ReferenceRangeChart), so the drop stays low for the
 * life of the page and is baked into any PDF exported from it.
 *
 * Whether a given page load hits it is a race against image decoding, which is
 * why it appears and disappears with browser-cache state rather than reliably.
 * Declaring the intrinsic size (the PNGs are 15x20) makes the first measurement
 * correct whether or not the bitmap has arrived, which removes the race.
 */
export const BLOOD_DROP_WIDTH = 15;
export const BLOOD_DROP_HEIGHT = 20;

export function bloodDropLabel(src: string): string {
    return `<img src="${src}" width="${BLOOD_DROP_WIDTH}" height="${BLOOD_DROP_HEIGHT}" />`;
}

/**
 * The data-label options that let a drop sit ON its value at either end of the
 * axis instead of being shoved inward.
 *
 * Every bar chart anchors the drop on the point and then shifts the label left
 * by 12px (`x: -12`), which with the label's default 5px padding centers the
 * 15px drop on the point: -12 + 5 + 7.5 = +0.5px. That arithmetic only holds
 * while Highcharts leaves the label where it was put. Data labels on a
 * bar/column series default to `overflow: 'justify'`, which pulls any label
 * overlapping the edge of the plot area back inside until it touches it
 * (Series.justifyDataLabel). A point ON the axis minimum — every value of 0 on
 * the charts whose axis starts at 0, and every out-of-range value, since all
 * three charts clamp those to the axis bound — puts the label 12px outside, so
 * it gets justified back and the drop lands about half its own width to the
 * RIGHT of the tick it is reporting. The same happens mirrored at the alarm end.
 *
 * `overflow: 'allow'` turns the justify pass off. `crop: false` is not optional
 * with it: without justify, alignDataLabel falls through to the crop branch,
 * which HIDES any label whose box is not entirely inside the plot area — which
 * is exactly the label we just allowed to hang over the edge.
 *
 * The overhang is about 7px, and it lands in the chart's own left/right margin
 * (none of these charts set marginLeft/marginRight, and the marker name and
 * value are in a separate table column outside the Highcharts container), so
 * nothing collides with it and `.highcharts-container`'s overflow:hidden does
 * not clip it. MvxRangeChart solves the same problem differently, by padding
 * its axis domain so a clamped value never reaches the edge in the first place.
 */
export const BLOOD_DROP_EDGE_OPTIONS = {
    overflow: 'allow' as 'allow',
    crop: false,
};
